// scans_manager.jsx
// What the scans actually say, product-wide: the rollup (trend, devices,
// geography, referrers, the splash funnel) on top, and a filterable raw feed
// underneath for the "what happened at 14:02" questions.
//
// Note on privacy: the backend never returns scan IP addresses — they are
// recorded for abuse investigation and are not part of any API response, here or
// in the product. There is deliberately nothing in this file that would show one.

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ScanLine, RefreshCw, Globe, Smartphone, ListFilter, User } from 'lucide-react';
import { useQRCreate } from '../../authentication_components/qrCreateContext';
import {
  Section, Button, Loading, EmptyState, Pagination, Badge, ScanRollup,
  inputClass, fmtInt, fmtDateTime,
} from './qrcreate_ui';

const PAGE_SIZE = 50;

const WINDOWS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last 12 months' },
];

const ROUTES = [
  { value: '', label: 'Any entry point' },
  { value: 'direct', label: 'Direct redirect' },
  { value: 'splash', label: 'Splash screen' },
];

export default function ScansManager({ onOpenUser }) {
  const { fetchScanAnalytics, fetchScans, fetchScanFilters } = useQRCreate();

  const [days, setDays] = useState(30);
  const [country, setCountry] = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [route, setRoute] = useState('');
  const [offset, setOffset] = useState(0);

  const [stats, setStats] = useState(null);
  const [feed, setFeed] = useState({ scans: [], total: 0 });
  const [options, setOptions] = useState({ countries: [], device_types: [] });

  const [statsLoading, setStatsLoading] = useState(true);
  const [feedLoading, setFeedLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchScanFilters()
      .then((data) => setOptions({ countries: data?.countries || [], device_types: data?.device_types || [] }))
      .catch(() => { /* the dropdowns simply stay minimal */ });
  }, [fetchScanFilters]);

  const loadStats = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setStatsLoading(true);
    try {
      setStats(await fetchScanAnalytics({ days }));
    } catch (err) {
      toast.error(err.message || 'Failed to load scan analytics');
    } finally {
      setStatsLoading(false);
      setRefreshing(false);
    }
  }, [fetchScanAnalytics, days]);

  const loadFeed = useCallback(async () => {
    setFeedLoading(true);
    try {
      const result = await fetchScans({
        days,
        country: country || undefined,
        device_type: deviceType || undefined,
        route: route || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setFeed({ scans: result?.scans || [], total: result?.total || 0 });
    } catch (err) {
      toast.error(err.message || 'Failed to load the scan feed');
    } finally {
      setFeedLoading(false);
    }
  }, [fetchScans, days, country, deviceType, route, offset]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadFeed(); }, [loadFeed]);

  const refresh = () => {
    setRefreshing(true);
    loadStats({ silent: true });
    loadFeed();
  };

  const resetFilters = () => { setCountry(''); setDeviceType(''); setRoute(''); setOffset(0); };

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-3 py-6 sm:px-4 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-800">Scan analytics</h2>
          <p className="text-sm text-slate-500">
            What every QR code in the product is doing in the wild — devices, geography and the splash funnel.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => { setOffset(0); setDays(Number(e.target.value)); }} className={`${inputClass} w-44`}>
            {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
          <Button variant="secondary" onClick={refresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <Section title="Product-wide rollup" subtitle={`Last ${days} days`} icon={ScanLine}>
        {statsLoading ? <Loading label="Loading scan analytics…" /> : <ScanRollup stats={stats} />}
      </Section>

      <Section title="Scan feed" subtitle="Newest first" icon={ListFilter} className="p-0">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-4">
          <select value={country} onChange={(e) => { setOffset(0); setCountry(e.target.value); }} className={`${inputClass} w-44`}>
            <option value="">Any country</option>
            {options.countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={deviceType} onChange={(e) => { setOffset(0); setDeviceType(e.target.value); }} className={`${inputClass} w-40`}>
            <option value="">Any device</option>
            {options.device_types.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={route} onChange={(e) => { setOffset(0); setRoute(e.target.value); }} className={`${inputClass} w-44`}>
            {ROUTES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {(country || deviceType || route) && (
            <Button variant="ghost" onClick={resetFilters}>Clear filters</Button>
          )}
        </div>

        {feedLoading && feed.scans.length === 0 ? (
          <Loading label="Loading scans…" />
        ) : feed.scans.length === 0 ? (
          <EmptyState
            icon={ScanLine}
            title="No scans match these filters"
            hint="Widen the window or clear a filter."
            action={<Button variant="secondary" onClick={resetFilters}>Clear filters</Button>}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3">Code</th>
                    <th className="px-4 py-3">Owner</th>
                    <th className="px-4 py-3">Device</th>
                    <th className="px-4 py-3">Location</th>
                    <th className="px-4 py-3">Entry</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {feed.scans.map((s) => (
                    <tr key={s.scan_id} className="hover:bg-sky-50/40">
                      <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDateTime(s.scanned_at)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-700">{s.code_name || '(deleted code)'}</div>
                        {s.referrer && <div className="max-w-xs truncate text-xs text-slate-400" title={s.referrer}>via {s.referrer}</div>}
                      </td>
                      <td className="px-4 py-3">
                        {s.user_id ? (
                          <button onClick={() => onOpenUser?.(s.user_id)} className="inline-flex items-center gap-1.5 text-slate-600 hover:text-sky-700" title="Open this account">
                            <User className="h-3.5 w-3.5 text-slate-400" /> {s.owner_email || s.user_id}
                          </button>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        <div className="flex items-center gap-1.5">
                          <Smartphone className="h-3.5 w-3.5 text-slate-400" />
                          {s.device_type || 'unknown'}
                        </div>
                        <div className="text-xs text-slate-400">
                          {[s.os_name, s.browser_name].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        <div className="flex items-center gap-1.5">
                          <Globe className="h-3.5 w-3.5 text-slate-400" />
                          {s.country || 'unknown'}
                        </div>
                        <div className="text-xs text-slate-400">{[s.city, s.region].filter(Boolean).join(', ') || '—'}</div>
                      </td>
                      <td className="px-4 py-3">
                        {s.scan_route === 'splash' ? (
                          <Badge className={s.redirected ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-amber-50 text-amber-700 ring-amber-200'}>
                            {s.redirected ? 'Splash · continued' : 'Splash · stopped'}
                          </Badge>
                        ) : (
                          <Badge className="bg-sky-50 text-sky-700 ring-sky-200">Direct</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination total={feed.total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} noun="scan" />
          </>
        )}
      </Section>

      <p className="px-1 text-xs text-slate-400">
        {fmtInt(feed.total)} scan{feed.total === 1 ? '' : 's'} in this window. Scan IP addresses are recorded for abuse
        investigation only and are never exposed through the API or this dashboard.
      </p>
    </div>
  );
}
